import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Send, User, Clock, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveChats = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [pendingChats, setPendingChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);

  useEffect(() => {
    fetchPendingChats();
    
    const interval = setInterval(() => {
      fetchPendingChats();
      if (activeChat) {
        fetchChatMessages(activeChat.session_id);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [activeChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchPendingChats = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/admin/chat/pending`, {
        headers: getAuthHeader()
      });
      setPendingChats(response.data);
    } catch (error) {
      console.error('Failed to fetch pending chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatMessages = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chatbot/admin/chat/${sessionId}/messages`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleClaimChat = async (chat) => {
    try {
      await axios.post(
        `${API}/chatbot/admin/chat/${chat.session_id}/assign`,
        {},
        { headers: getAuthHeader() }
      );
      
      setActiveChat(chat);
      await fetchChatMessages(chat.session_id);
      toast({ title: "Success", description: "Chat assigned to you" });
      fetchPendingChats(); // Refresh list
    } catch (error) {
      console.error('Failed to claim chat:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to claim chat",
        variant: "destructive"
      });
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !activeChat) return;

    try {
      await axios.post(
        `${API}/chatbot/admin/chat/${activeChat.session_id}/message`,
        { message: inputMessage },
        { headers: getAuthHeader() }
      );
      
      setInputMessage('');
      await fetchChatMessages(activeChat.session_id);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Live Chats</h1>
          <p className="body-large">Manage customer conversations in real-time</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px', height: 'calc(100vh - 200px)' }}>
        {/* Chat List */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px', overflowY: 'auto' }}>
          <h2 className="heading-3" style={{ marginBottom: '16px' }}>
            Pending Chats ({pendingChats.length})
          </h2>
          
          {pendingChats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
              <MessageSquare size={48} style={{ opacity: 0.5, marginBottom: '12px' }} />
              <p className="body-medium">No pending chats</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pendingChats.map((chat) => (
                <div
                  key={chat.session_id}
                  onClick={() => handleClaimChat(chat)}
                  style={{
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: activeChat?.session_id === chat.session_id ? '#f3f4f6' : 'white'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = activeChat?.session_id === chat.session_id ? '#f3f4f6' : 'white'}
                >
                  <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                    <User size={16} style={{ color: '#A08EA8', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <p className="body-small" style={{ fontWeight: '600', marginBottom: '4px' }}>
                        {chat.lead_data?.name || 'Anonymous'}
                      </p>
                      <p className="body-small" style={{ color: '#6b7280', fontSize: '12px' }}>
                        {chat.selected_service}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Clock size={12} style={{ color: '#9ca3af' }} />
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {formatTime(chat.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat Window */}
        <div style={{ background: 'white', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {activeChat ? (
            <>
              {/* Chat Header */}
              <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)', color: 'white' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <User size={24} />
                  <div>
                    <h3 className="heading-3" style={{ margin: 0, color: 'white' }}>
                      {activeChat.lead_data?.name || 'Anonymous'}
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>
                      {activeChat.lead_data?.email} • {activeChat.lead_data?.phone}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
                      Service: {activeChat.selected_service}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f9fafb' }}>
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: '16px',
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-start' : 'flex-end'
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '70%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        backgroundColor: msg.role === 'user' ? 'white' : '#A08EA8',
                        color: msg.role === 'user' ? '#333' : 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      {msg.role === 'agent' && (
                        <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '4px' }}>
                          You
                        </div>
                      )}
                      {msg.role === 'system' && (
                        <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '4px', fontStyle: 'italic' }}>
                          System
                        </div>
                      )}
                      <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>{msg.content}</p>
                      <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
                        {formatTime(msg.timestamp)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} style={{ padding: '16px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!inputMessage.trim()}
                    style={{
                      background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: inputMessage.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontWeight: '600',
                      opacity: inputMessage.trim() ? 1 : 0.5
                    }}
                  >
                    <Send size={16} />
                    Send
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              <div style={{ textAlign: 'center' }}>
                <MessageSquare size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <p className="body-large">Select a chat to start conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveChats;
